import { loadStripe, type Stripe } from '@stripe/stripe-js'

/**
 * Memoized Stripe.js loader for the public checkout (U8, KTD3).
 *
 * Stripe.js must be initialized with the CONNECTED account id (`stripeAccount`)
 * so the Payment Element mounts in the connected-account context — the school's
 * account, not our platform account. That means the cache key has to be the
 * (publishable key, connected account) pair together, not the key alone: two
 * checkouts against different schools in the same browser session must not
 * share a Stripe instance scoped to the wrong account.
 *
 * Stripe is optional at the platform level (the backend only throws lazily,
 * on first Stripe call — `apps/backend/src/interfaces/stripe/index.ts` — and
 * only paid-event checkout ever reaches that call). This mirrors that
 * posture client-side: when `NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY` is unset,
 * checkout no-ops (returns null) instead of throwing, so an unconfigured
 * environment degrades gracefully rather than crashing the public event page.
 */
const stripePromiseCache = new Map<string, Promise<Stripe | null>>()

export function isStripeCheckoutConfigured(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)
}

/**
 * Returns a memoized `Promise<Stripe | null>` scoped to `stripeAccountId`, or
 * `null` when the publishable key is not configured (see `isStripeCheckoutConfigured`).
 */
export function getStripeClient(stripeAccountId: string): Promise<Stripe | null> | null {
  const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
  if (!publishableKey) return null

  const cacheKey = `${publishableKey}:${stripeAccountId}`
  const cached = stripePromiseCache.get(cacheKey)
  if (cached) return cached

  const promise = loadStripe(publishableKey, { stripeAccount: stripeAccountId })
  stripePromiseCache.set(cacheKey, promise)
  return promise
}
